import { useDanceStyles } from "@/hooks/useDanceStyles";
import { cn } from "@/lib/utils";

interface DanceStyleBadgeProps {
  slug: string;
  size?: "sm" | "md";
  className?: string;
}

/** Text colour that stays readable on a style's colour. */
export function getContrastColor(hex: string): string {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const toLinear = (c: number) => c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  const L = 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
  return 1.05 / (L + 0.05) >= 4.5 ? "#ffffff" : "#1a1a2e";
}

export function DanceStyleBadge({ slug, size = "sm", className }: DanceStyleBadgeProps) {
  const { styleMap } = useDanceStyles();
  const style = styleMap[slug];
  if (!style) return null;

  const textColor = getContrastColor(style.color);

  return (
    <span
      className={cn(
        "inline-flex items-center font-medium rounded-md whitespace-nowrap",
        size === "sm" && "px-2 py-0.5 text-[11px]",
        size === "md" && "px-2.5 py-1 text-xs",
        className
      )}
      style={{
        backgroundColor: style.color,
        color: textColor,
      }}
    >
      {style.name}
    </span>
  );
}

/**
 * An event's style badges in display order: its main styles first, then the
 * rest in the site's list order. Unknown slugs render nothing, as above.
 */
export function EventStyleBadges({
  danceStyles,
  mainStyles,
  size = "sm",
  limit,
}: {
  danceStyles: readonly string[];
  mainStyles?: readonly string[];
  size?: "sm" | "md";
  limit?: number;
}) {
  const { orderStyles } = useDanceStyles();
  const ordered = orderStyles(danceStyles, mainStyles);
  return (
    <>
      {(limit ? ordered.slice(0, limit) : ordered).map((slug) => (
        <DanceStyleBadge key={slug} slug={slug} size={size} />
      ))}
    </>
  );
}
